import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';

import { FormsModule, NgModel } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TooltipModule } from 'primeng/tooltip';
import { accessRightDefinition } from '../../../entity-definitions/access-right.definition';
import { AccessRight, AccessRightDomain } from '../../../types/access-right.interface';
import { Asset } from '../../../types/asset.interface';
import { Role } from '../../../types/role.interface';
import { User } from '../../../types/user.interface';
import { AssetGroupSelectItem } from '../../../utils/asset-grouping.util';
import { buildDeleteConfig } from '../../../utils/delete-confirmation';
import { CesDeleteButtonComponent } from '../../ui/ces-buttons';
import { CesDialogFooterComponent } from '../../ui/dialog-footer/dialog-footer.component';
import { CesSelectFieldComponent } from '../../ui/ces-select-field/ces-select-field.component';

import { CesTooltipAutoDismissDirective } from '../../../directives/ces-tooltip-auto-dismiss.directive';
interface AccessRightFormAdditionalData {
  Origin: 'settings' | 'asset';
  Role: Role[];
  Asset: Asset[];
  User: User[];
  AccessRights: AccessRightDomain[];
  SelectedAccessRights?: AccessRightDomain[];
  GroupedAssets?: AssetGroupSelectItem[];
}

interface AccessRightFormDialogData {
  additionalData: AccessRightFormAdditionalData;
  mode: 'create' | 'update' | 'bulk-edit';
  entity?: AccessRightDomain;
  onDelete?: (entity: AccessRightDomain | null) => void;
}

@Component({
  selector: 'app-access-right-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CesTooltipAutoDismissDirective,
    CesDeleteButtonComponent,
    CesDialogFooterComponent,
    FormsModule,
    CesSelectFieldComponent,
    TranslocoModule,
    TooltipModule,
  ],
  templateUrl: './access-right-form.component.html',
  styleUrl: './access-right-form.component.css',
})
export class AccessRightFormComponent implements OnInit, AfterViewInit {
  mode: 'create' | 'update' | 'bulk-edit' = 'create';
  // origin = from where this form is called (settings = users in settings page, asset = rights page in asset)
  origin: 'settings' | 'asset' = 'asset';
  definition = accessRightDefinition;

  @ViewChild('roleCtrl') roleCtrl!: NgModel;
  @ViewChild('userCtrl') userCtrl!: NgModel;

  currentAccessRightDomain: AccessRightDomain | null = null;
  selectedRole: number | null = null;
  selectedAsset: number | null = null;
  selectedAssets: number[] = [];
  selectedUser: number | null = null;
  accessRightDomains: AccessRightDomain[] = [];

  roles: Role[] = [];
  assets: Asset[] = [];
  groupedAssets: AssetGroupSelectItem[] = [];
  users: User[] = [];
  selectedAccessRights: AccessRightDomain[] = [];
  private readonly config = inject<DynamicDialogConfig<AccessRightFormDialogData>>(DynamicDialogConfig, { optional: true });
  private readonly dialogRef = inject(DynamicDialogRef, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);

  isFormValid = false;
  isAccessExisting = false;
  showAssetList = false;

  get isSyncMode(): boolean {
    return this.origin === 'settings' && this.mode === 'create';
  }

  get isAssetContextCreate(): boolean {
    return this.origin === 'asset' && this.mode === 'create';
  }

  get hasImplicitAsset(): boolean {
    return this.assets.length === 1;
  }

  get hasChanges(): boolean {
    return !this.isSyncMode || this.selectedAssets.length > 0;
  }

  ngOnInit(): void {
    if (!this.config?.data) return;

    const { additionalData, mode, entity } = this.config.data;
    this.origin = additionalData.Origin;
    this.roles = additionalData.Role;
    this.assets = additionalData.Asset;
    this.groupedAssets = additionalData.GroupedAssets ?? [];
    this.users = [...additionalData.User].sort((a, b) => a.displayName.localeCompare(b.displayName));
    this.accessRightDomains = additionalData.AccessRights;
    this.mode = mode;

    if (this.mode === 'bulk-edit' && additionalData.SelectedAccessRights) {
      this.selectedAccessRights = additionalData.SelectedAccessRights;
    }

    if (this.isAssetContextCreate && this.hasImplicitAsset) {
      const implicit = this.assets[0];
      if (implicit) {
        this.users = this.excludeUsersAssignedTo(implicit.id);
        this.selectedAsset = implicit.id;
      }
    }

    if (entity) {
      this.currentAccessRightDomain = entity;
      this.selectedAsset = entity.assetId ?? null;
      this.selectedRole = entity.roleId ?? null;
      this.selectedUser = entity.userId ?? null;
    }
  }

  private excludeUsersAssignedTo(assetId: number | undefined): User[] {
    if (assetId == null) return this.users;
    const assignedUserIds = new Set(this.accessRightDomains.filter(ar => ar.assetId === assetId).map(ar => ar.userId));
    return this.users.filter(u => !assignedUserIds.has(u.id));
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.validate();
      this.cdr.markForCheck();
    });
  }

  submit() {
    if (this.mode === 'bulk-edit') {
      this.submitBulkEdit();
    } else if (this.isSyncMode) {
      this.submitSync();
    } else {
      this.submitStandard();
    }
  }

  private submitBulkEdit() {
    const result = { roleId: this.selectedRole };
    this.closeDialog(result);
  }

  private submitSync() {
    const primary = this.users[0];
    if (!primary) return;
    this.closeDialog({
      syncMode: true,
      roleId: this.selectedRole,
      userId: primary.id,
      toAdd: [...this.selectedAssets],
      toRemove: [],
    });
  }

  private submitStandard() {
    const accessRight = this.createAccessRight();
    if (!accessRight) return;
    this.closeDialog(accessRight);
  }
  cancel() {
    this.dialogRef?.close(false);
  }
  delete() {
    const onDelete = this.config?.data?.onDelete;
    if (onDelete && typeof onDelete === 'function') {
      onDelete(this.currentAccessRightDomain);
      this.closeDialog();
    }
  }

  protected deleteConfig() {
    const entity = this.currentAccessRightDomain;
    return buildDeleteConfig(this.definition.messages.confirmDelete?.(), (entity && this.definition.deleteName?.(entity)) ?? '', {
      role: entity?.roleName ?? '',
    });
  }

  private createAccessRight(): AccessRight | null {
    const { selectedRole, selectedAsset, selectedUser } = this;
    if (selectedRole == null || selectedAsset == null || selectedUser == null) {
      return null;
    }
    return {
      roleId: selectedRole,
      assetId: selectedAsset,
      userId: selectedUser,
      name: this.currentAccessRightDomain?.name ?? '',
      id: this.currentAccessRightDomain?.id ?? 0,
    };
  }

  private closeDialog(result?: unknown) {
    this.dialogRef?.close(result);
  }

  onRoleChange() {
    this.validate();
  }

  toggleAssetList() {
    this.showAssetList = !this.showAssetList;
  }

  validate() {
    if (this.mode === 'bulk-edit') {
      this.validateBulkEdit();
    } else if (this.origin === 'settings') {
      this.validateSettings();
    } else {
      this.validateAssetMode();
    }
  }

  private validateBulkEdit() {
    this.isFormValid = this.roleCtrl?.valid ?? false;
    this.isAccessExisting = false;
  }

  private validateSettings() {
    this.isFormValid = this.roleCtrl?.valid ?? false;
    if (this.isSyncMode) {
      this.isFormValid = this.isFormValid && this.hasChanges;
    }
    this.isAccessExisting = false;
  }

  private validateAssetMode() {
    this.isAccessExisting = false;
    if (this.mode === 'create') {
      this.isAccessExisting = this.checkAccessExists();
      this.isFormValid = (this.userCtrl?.valid ?? false) && (this.roleCtrl?.valid ?? false) && !this.isAccessExisting;
      return;
    }
    this.isFormValid = this.roleCtrl?.valid ?? false;
  }

  private checkAccessExists(): boolean {
    return this.accessRightDomains?.some(ar => ar.userId === this.selectedUser && ar.assetId === this.selectedAsset) ?? false;
  }
}
